import { HHStack, HHTypography } from '@hinge-health/react-component-library';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Tab, Tabs, useTheme } from '@mui/material';
import { OrgGroup } from '../../utils/org-groups';

export interface TinTabItem {
  group: OrgGroup;
  legalName: string;
  /** True once this TIN's per-step decision is fully resolved. */
  ready: boolean;
  /**
   * True when this TIN has an unresolved conflict requiring operator action
   * (e.g. a KG match with conflicting attributes and no reuse/override
   * decision yet). Takes visual priority over `ready` — shows amber warning.
   */
  conflict?: boolean;
}

interface TinTabsProps {
  items: TinTabItem[];
  activeTin: string;
  onSelect: (tin: string) => void;
}

// Per-TIN tab strip rendered above the per-org cards on Merchant / Customer
// Account / Contract. The strip only appears for multi-TIN input; single-TIN
// pages skip rendering it. The check-glyph turns green once the TIN's per-step
// decision is complete so multi-TIN runs can scan progress at a glance.
export const TinTabs = ({
  items,
  activeTin,
  onSelect,
}: TinTabsProps): JSX.Element | null => {
  const theme = useTheme();
  if (items.length <= 1) return null;
  return (
    <Tabs
      value={activeTin}
      onChange={(_, v): void => onSelect(v as string)}
      variant="scrollable"
      scrollButtons="auto"
      sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
    >
      {items.map(item => {
        const tin = item.group.tax_id;
        return (
          <Tab
            key={tin}
            value={tin}
            sx={{ textTransform: 'none', minHeight: 44, py: 0.5 }}
            label={
              <HHStack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                {item.conflict === true ? (
                  <WarningAmberIcon
                    fontSize="small"
                    sx={{ color: theme.palette.warning.main }}
                  />
                ) : item.ready ? (
                  <CheckCircleOutlineIcon
                    fontSize="small"
                    sx={{ color: theme.palette.success.main }}
                  />
                ) : (
                  <RadioButtonUncheckedIcon
                    fontSize="small"
                    sx={{ color: theme.palette.text.disabled }}
                  />
                )}
                <HHStack spacing={0} alignItems="flex-start">
                  <HHTypography
                    hhVariant="body2"
                    sx={{ fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {item.legalName}
                  </HHTypography>
                  <HHTypography
                    hhVariant="caption"
                    sx={{
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                      fontSize: 11,
                      opacity: 0.7,
                      lineHeight: 1.2,
                    }}
                  >
                    TIN {tin} · {item.group.locations.length} loc
                  </HHTypography>
                </HHStack>
              </HHStack>
            }
          />
        );
      })}
    </Tabs>
  );
};
